import { useLazyQuery } from '@vue/apollo-composable';
import { useDict, useFischer } from 'composables/fischerPlugin';
import { useUser } from 'composables/user';
import GET_CONSTRUCTION_AREA from 'gql/getConstructionArea.gql';
import GET_CONSTRUCTION_ROOM from 'gql/getConstructionRoom.gql';
import GET_CONSTRUCTION_SITE from 'gql/getConstructionSite.gql';
import { useConstructionDocumentationStore } from 'store/construction-documentation';
import { computed } from 'vue';

export const useConstructionDocumentation = () => {
  const projectType = 'cd';
  const fischer = useFischer();
  const dict = useDict();
  const cdStore = useConstructionDocumentationStore();
  const { logoutIfTokenInvalid } = useUser();

  const pageUrl = fischer.pages.constructionDocumentation()?.url;

  const setBrowserPageTitle = (pageTitle: string | undefined) => {
    const title = document.querySelector('head title');
    if (title) {
      if (pageTitle) {
        title.textContent = `${pageTitle} - ${dict.get('construction-Projects.Breadcrumb.Construction-Documentation')} | ${fischer.brandName()}`;
      } else {
        title.textContent = `${dict.get('construction-Projects.Breadcrumb.Construction-Documentation')} | ${fischer.brandName()}`;
      }
    }
  };

  const formatNameForUrl = (name: string) => {
    return name
      .replace(/[^A-Za-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+$/, '')
      .toLowerCase();
  };

  const getPathString = (pageType: string) => {
    const data: any = cdStore[pageType as keyof typeof cdStore];
    const name = formatNameForUrl(data?.name);
    const id = cdStore[`${pageType}Id` as keyof typeof cdStore];
    return encodeURI(`/${name}_${id}`);
  };

  const getCDUrl = (targetName: string, targetId: string, targetType?: string) => {
    const name = formatNameForUrl(targetName);
    const host = cdStore.isArchive ? pageUrl + '/archive' : pageUrl;
    const id = targetId.replace(/.*:/, '');

    if (targetType === 'area') {
      return host + getPathString('project') + encodeURI(`/${name}_${id}`);
    }

    if (targetType === 'room') {
      return host + getPathString('project') + getPathString('area') + encodeURI(`/${name}_${id}`);
    }

    return host + encodeURI(`/${name}_${targetId}`);
  };

  const graphQlVariables = computed(() => {
    const areaId = cdStore.areaId ? `${cdStore.projectId}:${cdStore.areaId}` : null;
    const roomId = cdStore.roomId
      ? `${cdStore.projectId}:${cdStore.areaId}:${cdStore.roomId}`
      : null;

    return {
      siteId: cdStore.projectId,
      areaId,
      roomId,
      impersonatedUser: fischer.user.getAssistedServiceEmail(),
      accessToken: fischer.user.accessToken(),
      language: fischer.siteLanguage(),
      project: projectType,
    };
  });

  const {
    load: loadProject,
    onError: onProjectError,
    refetch: refetchProjectData,
  } = useLazyQuery(GET_CONSTRUCTION_SITE, graphQlVariables.value);

  onProjectError((err: any) => {
    console.error('could not load construction documentation project', err);

    const { graphQLErrors } = err;
    if (graphQLErrors.some((e: any) => e.extensions.code === 'NOT_PERMITTED')) {
      window.location.href = fischer.pages.constructionDocumentation().url;
      return;
    }

    logoutIfTokenInvalid(err);
  });

  const {
    load: loadArea,
    onError: onAreaError,
    refetch: refetchArea,
  } = useLazyQuery(GET_CONSTRUCTION_AREA, graphQlVariables.value);

  onAreaError((err: any) => {
    console.error('could not load construction documentation area', err);
  });

  const {
    load: loadRoom,
    onError: onRoomError,
    refetch: refetchRoom,
  } = useLazyQuery(GET_CONSTRUCTION_ROOM, graphQlVariables.value);

  onRoomError((err: any) => {
    console.error('could not load construction documentation room', err);
  });

  const loadConstructionDocumentationData = async () => {
    const promises = [];

    if (cdStore.projectId) {
      promises.push(loadProject());
    }

    if (cdStore.areaId) {
      promises.push(loadArea());
    }

    if (cdStore.roomId) {
      promises.push(loadRoom());
    }

    if (!promises.length) {
      return;
    }

    try {
      cdStore.isLoading = true;
      const [project, area, room] = await Promise.all(promises);
      cdStore.project = project?.constructionSite;
      cdStore.area = area?.constructionArea;
      cdStore.room = room?.constructionRoom;
      cdStore.isArchive = project?.constructionSite?.archived;
    } catch (err) {
      console.error('could not fetch construction documentation data', err);
    } finally {
      cdStore.isLoading = false;
    }
  };

  const getConstructionBreadcrumb = computed(() => {
    if (!cdStore || IS_SSR || !window.location.href.includes(pageUrl)) {
      return null;
    }

    const breadcrumbs = [];
    const host = cdStore.isArchive ? pageUrl + '/archive' : pageUrl;

    if (IS_SHOWROOM && window.location.href.includes(pageUrl)) {
      breadcrumbs.push({
        url: pageUrl,
        title:
          dict.get('construction-Projects.Breadcrumb.Construction-Documentation') ||
          'Construction Documentation',
      });
    }

    if (cdStore.isArchive) {
      breadcrumbs.push({
        url: host,
        title: dict.get('construction-Projects.Breadcrumb.Archive') || 'Archive',
      });
    }

    if (cdStore.project) {
      breadcrumbs.push({
        url: host + getPathString('project'),
        title: cdStore.project.name,
      });
    }

    if (cdStore.area) {
      breadcrumbs.push({
        url: host + getPathString('project') + getPathString('area'),
        title: cdStore.area.name,
      });
    }

    if (cdStore.room) {
      breadcrumbs.push({
        url: host + getPathString('project') + getPathString('area') + getPathString('room'),
        title: cdStore.room.name,
      });
    }

    if (cdStore.firestop) {
      breadcrumbs.push({
        url:
          host +
          getPathString('project') +
          getPathString('area') +
          getPathString('room') +
          getPathString('firestop'),
        title: cdStore.firestop.name,
      });
    }

    if (cdStore.isAuditLogPage && !cdStore.isLoading) {
      // url can be empty because always last element
      breadcrumbs.push({
        url: '',
        title: dict.get('construction-Projects.Breadcrumb.Audit-Log') || 'Audit Log',
      });
    }

    if (cdStore.isDocumentsPage && !cdStore.isLoading) {
      // url can be empty because always last element
      breadcrumbs.push({
        url: '',
        title: dict.get('construction-Projects.Breadcrumb.Documents') || 'Documents',
      });
    }

    return breadcrumbs;
  });

  const refetchProject = async () => {
    try {
      await loadProject();
      const result = await refetchProjectData();
      cdStore.project = result?.data?.constructionSite;
    } catch (err) {
      console.error('could not fetch construction documentation project data', err);
    }
  };

  return {
    pageUrl,
    setBrowserPageTitle,
    loadProject,
    refetchProject,
    loadArea,
    refetchArea,
    loadRoom,
    refetchRoom,
    getCDUrl,
    loadConstructionDocumentationData,
    getConstructionBreadcrumb,
  };
};
